import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle, CheckCircle2, LifeBuoy, Loader2, Mail, MessageSquare,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { formatDate } from "@/lib/dates";

/**
 * The human escalation queue (DY2.4, DANY.md §2.7).
 *
 * Deliberately a queue and not an inbox. FixyDany has no live chat — that was
 * decided, not deferred: there is no operation to staff one. So the whole job
 * this screen supports is four moves: see what is waiting, read the
 * conversation it came out of, answer by email, set the state.
 *
 * **The transcript is not a "details" affordance.** The summary is Dany's
 * account of somebody's problem, and the only way to check it against what the
 * person actually said is to have both on one screen. That is why opening a
 * ticket loads the thread with it rather than linking somewhere.
 *
 * **Writing a reply sends it.** There is no draft state and no second button.
 * A reply saved but not delivered is the exact failure this whole feature
 * exists to prevent, and it would be invisible from here — so the backend
 * refuses to record one it could not email, and this screen surfaces that
 * refusal instead of a success.
 *
 * **The metrics strip is the improvement cycle, not decoration.** DANY.md §2.7
 * closes on it: every ticket is a case the corpus or the tools should have
 * covered tomorrow. Grouped by trigger and by category because the two answer
 * different questions — trigger says whether Dany knew she was out of her
 * depth, category says what the platform is failing at.
 */
const STATUSES = ["open", "in_progress", "waiting_user", "resolved", "closed"];
const CATEGORIES = ["billing", "account", "legal_privacy", "security", "other"];
const TRIGGERS = [
  "user_request", "sensitive_category", "repeated_failure", "out_of_scope",
];

export default function AdminTickets() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [filter, setFilter] = useState({ status: "", category: "" });
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.set("status", filter.status);
      if (filter.category) params.set("category", filter.category);
      const [queue, stats] = await Promise.all([
        api.get(`/admin/tickets?${params.toString()}`),
        api.get("/admin/tickets/metrics"),
      ]);
      setTickets(queue.data.tickets || []);
      setMetrics(stats.data);
    } catch (e) {
      console.error("[admin] could not load the ticket queue:", e);
    }
  }, [filter.status, filter.category]);

  useEffect(() => { load(); }, [load]);

  /**
   * Open the ticket the URL names (T18-M6).
   *
   * The "Open the queue" button in the new-ticket email carries `?ticket=`, so
   * the case the email is about is on screen without anyone scanning the list
   * for an id they were just reading in another window.
   *
   * Spent once per id, through a ref rather than by clearing the URL: closing
   * the detail must not reopen it on the next render, and the address stays
   * shareable and reloadable. An id that no longer resolves fails inside
   * `openTicket`, which leaves the queue on screen — the right answer to a link
   * clicked a week later, and the reason this does not need to check first.
   */
  const honoredTicket = useRef(null);
  useEffect(() => {
    const wanted = searchParams.get("ticket");
    if (!wanted || honoredTicket.current === wanted) return;
    honoredTicket.current = wanted;
    openTicket(wanted);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTicket = async (ticketId) => {
    setError(""); setNotice(""); setReply("");
    try {
      const { data } = await api.get(`/admin/tickets/${ticketId}`);
      setOpen(data);
    } catch (e) {
      setError(errorMessage(e, t("adminTickets.failed")));
    }
  };

  const update = async ({ status, response }) => {
    setBusy(true); setError(""); setNotice("");
    try {
      const { data } = await api.patch(`/admin/tickets/${open.ticket_id}`, {
        status: status || null,
        response: response || null,
      });
      // Keep the transcript: the PATCH answer carries the ticket, not the
      // conversation, and losing it on every state change would make the one
      // thing worth reading disappear the moment you act on it.
      setOpen({ ...open, ...data });
      setReply("");
      setNotice(data.emailed
        ? t("adminTickets.emailed", { email: data.contact_email })
        : t("adminTickets.statusSaved"));
      await load();
    } catch (e) {
      setError(errorMessage(e, t("adminTickets.failed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout role="admin">
      <div data-testid="admin-tickets">
        <div className="flex items-center gap-3 mb-2">
          <LifeBuoy size={22} className="text-hive-ink" />
          <h1 className="font-display text-3xl font-bold text-worker">
            {t("adminTickets.title")}
          </h1>
        </div>
        <p className="text-smoke mb-8 max-w-2xl">{t("adminTickets.intro")}</p>

        {metrics && <Metrics metrics={metrics} t={t} />}

        <div className="flex flex-wrap gap-2 mb-4">
          <Filter
            label={t("adminTickets.allStatuses")} value=""
            active={filter.status} onPick={(v) => setFilter({ ...filter, status: v })}
          />
          {STATUSES.map((s) => (
            <Filter
              key={s} label={t(`adminTickets.status.${s}`)} value={s}
              active={filter.status} onPick={(v) => setFilter({ ...filter, status: v })}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-6">
          <Filter
            label={t("adminTickets.allCategories")} value=""
            active={filter.category}
            onPick={(v) => setFilter({ ...filter, category: v })}
          />
          {CATEGORIES.map((c) => (
            <Filter
              key={c} label={t(`adminTickets.category.${c}`)} value={c}
              active={filter.category}
              onPick={(v) => setFilter({ ...filter, category: v })}
            />
          ))}
        </div>

        <div className="card-base overflow-x-auto max-w-5xl">
          <table className="w-full text-sm" data-testid="ticket-queue">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-smoke font-display">
                <th className="p-3">{t("adminTickets.colWhen")}</th>
                <th className="p-3">{t("adminTickets.colWho")}</th>
                <th className="p-3">{t("adminTickets.colSubject")}</th>
                <th className="p-3">{t("adminTickets.colCategory")}</th>
                <th className="p-3">{t("adminTickets.colTrigger")}</th>
                <th className="p-3">{t("adminTickets.colStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => (
                <tr
                  key={ticket.ticket_id}
                  className="border-t border-sand cursor-pointer hover:bg-linen"
                  onClick={() => openTicket(ticket.ticket_id)}
                  data-testid={`ticket-row-${ticket.ticket_id}`}
                >
                  <td className="p-3 whitespace-nowrap text-smoke">
                    {formatDate(ticket.created_at)}
                  </td>
                  <td className="p-3">{ticket.user_email || ticket.contact_email}</td>
                  <td className="p-3 font-display font-semibold text-worker">
                    {ticket.subject}
                  </td>
                  <td className="p-3">{t(`adminTickets.category.${ticket.category}`)}</td>
                  <td className="p-3 text-smoke">
                    {t(`adminTickets.trigger.${ticket.trigger_reason}`)}
                  </td>
                  <td className="p-3">
                    <span className="chip">
                      {t(`adminTickets.status.${ticket.status}`)}
                    </span>
                  </td>
                </tr>
              ))}
              {!tickets.length && (
                <tr>
                  <td className="p-6 text-smoke" colSpan={6}>
                    {t("adminTickets.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {open && (
          <Detail
            ticket={open} t={t} busy={busy} error={error} notice={notice}
            reply={reply} setReply={setReply}
            onClose={() => setOpen(null)} onUpdate={update}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function Filter({ label, value, active, onPick }) {
  return (
    <button
      type="button"
      onClick={() => onPick(value)}
      className={`chip ${active === value ? "bg-hive/20 border-hive text-worker" : ""}`}
      data-testid={`ticket-filter-${value || "all"}`}
    >
      {label}
    </button>
  );
}

/**
 * Where the escalations pile up.
 *
 * Two rows and no chart. The counts are small by construction — a queue a
 * person works through cannot grow past what a person can read — and at that
 * size a bar chart is a decorative way of showing four numbers.
 */
function Metrics({ metrics, t }) {
  const cell = (label, value) => (
    <div key={label} className="px-4 py-2">
      <p className="text-xs uppercase tracking-widest text-smoke font-display">
        {label}
      </p>
      <p className="font-mono text-xl text-worker">{value}</p>
    </div>
  );
  return (
    <div className="card-base p-4 mb-6 max-w-5xl" data-testid="ticket-metrics">
      <div className="flex flex-wrap divide-x divide-sand">
        {cell(t("adminTickets.openNow"), metrics.open_now ?? 0)}
        {cell(t("adminTickets.lastWeek"), metrics.last_7_days ?? 0)}
      </div>
      <p className="mt-4 mb-1 text-xs uppercase tracking-widest text-smoke font-display">
        {t("adminTickets.byTrigger")}
      </p>
      <div className="flex flex-wrap gap-2">
        {TRIGGERS.map((k) => (
          <span key={k} className="chip" data-testid={`metric-trigger-${k}`}>
            {t(`adminTickets.trigger.${k}`)} · {metrics.by_trigger?.[k] || 0}
          </span>
        ))}
      </div>
      <p className="mt-4 mb-1 text-xs uppercase tracking-widest text-smoke font-display">
        {t("adminTickets.byCategory")}
      </p>
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((k) => (
          <span key={k} className="chip" data-testid={`metric-category-${k}`}>
            {t(`adminTickets.category.${k}`)} · {metrics.by_category?.[k] || 0}
          </span>
        ))}
      </div>
    </div>
  );
}

function Detail({
  ticket, t, busy, error, notice, reply, setReply, onClose, onUpdate,
}) {
  return (
    <div className="card-base p-6 mt-8 max-w-4xl" data-testid="ticket-detail">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold text-worker">
            {ticket.subject}
          </h2>
          <p className="text-xs text-smoke mt-1">
            {ticket.ticket_id} · {t(`adminTickets.category.${ticket.category}`)} ·{" "}
            {t(`adminTickets.trigger.${ticket.trigger_reason}`)}
          </p>
        </div>
        <button onClick={onClose} className="fx-btn fx-btn--ghost text-sm"
                data-testid="ticket-close">
          {t("adminTickets.close")}
        </button>
      </div>

      <p className="mt-4 whitespace-pre-wrap text-sm text-worker bg-linen rounded-lg p-4">
        {ticket.summary}
      </p>

      <p className="mt-4 text-sm text-smoke flex items-center gap-2">
        <Mail size={15} />
        {t("adminTickets.repliesGoTo", { email: ticket.contact_email })}
      </p>

      {/* The conversation the summary came out of. Collapsed by default because
          it is long and the summary is usually enough — but one click away,
          never a different page: checking Dany's account of the problem against
          the person's own words is the thing this screen exists to make easy. */}
      {!!(ticket.messages || []).length && (
        <details className="mt-4" data-testid="ticket-transcript">
          <summary className="cursor-pointer text-sm font-display font-semibold text-worker flex items-center gap-2">
            <MessageSquare size={15} />
            {t("adminTickets.transcript", { n: ticket.messages.length })}
          </summary>
          <div className="mt-3 max-h-96 overflow-y-auto space-y-2">
            {ticket.messages
              .filter((m) => m.role === "user" || m.role === "assistant")
              .map((m, i) => (
                <div key={m.message_id || i}
                     className={`text-sm rounded-lg p-3 ${
                       m.role === "user" ? "bg-linen" : "bg-white border border-sand"
                     }`}>
                  <p className="text-xs uppercase tracking-widest text-smoke font-display mb-1">
                    {t(`adminTickets.role.${m.role}`)}
                  </p>
                  <p className="whitespace-pre-wrap text-worker">{m.content}</p>
                </div>
              ))}
          </div>
        </details>
      )}

      {ticket.admin_response && (
        <div className="mt-5">
          <p className="text-xs uppercase tracking-widest text-smoke font-display mb-1">
            {t("adminTickets.lastReply")}
          </p>
          <p className="whitespace-pre-wrap text-sm text-worker bg-linen rounded-lg p-4">
            {ticket.admin_response}
          </p>
        </div>
      )}

      <div className="mt-6">
        <label className="label-base">{t("adminTickets.replyLabel")}</label>
        <textarea
          className="input-base w-full"
          rows={5}
          maxLength={8000}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder={t("adminTickets.replyPlaceholder")}
          data-testid="ticket-reply"
        />
        <p className="text-xs text-smoke mt-1">{t("adminTickets.replyHint")}</p>
      </div>

      {error && (
        <p className="mt-4 text-sm text-red-700 flex items-start gap-1"
           data-testid="ticket-error">
          <AlertCircle size={15} className="shrink-0 mt-px" />{error}
        </p>
      )}
      {notice && (
        <p className="mt-4 text-sm text-green-700 flex items-start gap-1"
           data-testid="ticket-notice">
          <CheckCircle2 size={15} className="shrink-0 mt-px" />{notice}
        </p>
      )}

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => onUpdate({ response: reply })}
          disabled={busy || !reply.trim()}
          className="fx-btn fx-btn--primary"
          data-testid="ticket-send-reply"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
          {t("adminTickets.sendReply")}
        </button>
        <select
          className="input-base w-auto"
          value={ticket.status}
          onChange={(e) => onUpdate({ status: e.target.value })}
          disabled={busy}
          data-testid="ticket-status"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{t(`adminTickets.status.${s}`)}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
